import React, { useRef, useState, useCallback, useEffect } from 'react';
import s from './TransformationReveal.module.css';

/* Matched by actual room — same physical space before/after */
import before1 from '../../images/הבית של גילה לפני 1.webp';
import after1  from '../../images/הבית של גילה 8.webp';

import salonBefore   from '../../images/trans-salon-before.webp';
import salonAfter    from '../../images/trans-salon-after.webp';
import kitchenBefore from '../../images/trans-kitchen-before.webp';
import kitchenAfter  from '../../images/trans-kitchen-after.webp';
import stairsBefore  from '../../images/trans-stairs-before.webp';
import stairsAfter   from '../../images/trans-stairs-after.webp';
import courtBefore   from '../../images/trans-court-before.webp';
import courtAfter    from '../../images/trans-court-after.webp';
import facadeBefore  from '../../images/trans-facade-before.webp';
import facadeAfter   from '../../images/trans-facade-after.webp';

const PROJECTS = [
  { id: 1, title: 'הבית של גילה', location: 'חלל מגורים', before: before1, after: after1 },
  { id: 2, title: 'סלון ומרחב אירוח', location: 'שיפוץ ועיצוב מלא', before: salonBefore, after: salonAfter },
  { id: 3, title: 'מטבח ומרחב אוכל', location: 'תכנון ובנייה מלאים', before: kitchenBefore, after: kitchenAfter },
  { id: 4, title: 'גרם המדרגות', location: 'תכנון ובנייה מלאים', before: stairsBefore, after: stairsAfter },
  { id: 5, title: 'חצר וכניסה', location: 'תכנון ובנייה מלאים', before: courtBefore, after: courtAfter },
  { id: 6, title: 'חזית הבית', location: 'תכנון ובנייה מלאים', before: facadeBefore, after: facadeAfter },
];

function TransformationCard({ project }) {
  const [position, setPosition] = useState(52);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);

  const updateFromClient = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // RTL: right edge = 0%, left edge = 100%
    const offsetFromRight = rect.right - clientX;
    const pct = Math.max(0, Math.min(100, (offsetFromRight / rect.width) * 100));
    setPosition(pct);
  }, []);

  useEffect(() => {
    const onUp = () => setDragging(false);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  const showingBefore = position >= 50;

  return (
    <article className={s.card}>
      <div
        ref={containerRef}
        className={`${s.stage} ${dragging ? s.dragging : ''}`}
        onMouseDown={(e) => { setDragging(true); updateFromClient(e.clientX); }}
        onMouseMove={(e) => { if (dragging) updateFromClient(e.clientX); }}
        onMouseLeave={() => setDragging(false)}
        onTouchStart={(e) => { setDragging(true); updateFromClient(e.touches[0].clientX); }}
        onTouchMove={(e) => { if (dragging) updateFromClient(e.touches[0].clientX); }}
      >
        {/* After - base */}
        <div className={s.baseLayer}>
          <img src={project.after} alt="אחרי" draggable={false} loading="lazy" decoding="async" />
        </div>

        {/* Before - revealed from the right */}
        <div
          className={s.revealLayer}
          style={{ clipPath: `inset(0 0 0 ${100 - position}%)` }}
        >
          <img src={project.before} alt="לפני" draggable={false} loading="lazy" decoding="async" />
        </div>

        {/* Single active label (fades between states) */}
        <span className={`${s.label} ${showingBefore ? s.labelVisible : ''}`}>
          לפני
        </span>
        <span className={`${s.label} ${s.labelAfter} ${!showingBefore ? s.labelVisible : ''}`}>
          אחרי
        </span>

        {/* Slider handle */}
        <div
          className={s.handle}
          style={{ left: `${100 - position}%` }}
          aria-hidden="true"
        >
          <span className={s.handleLineTop} />
          <span className={s.handleKnob}>
            <span className={s.handleArrow}>‹</span>
            <span className={s.handleDot} />
            <span className={s.handleArrow}>›</span>
          </span>
          <span className={s.handleLineBottom} />
        </div>
      </div>
    </article>
  );
}

export default function TransformationReveal({ onOpenLead }) {
  return (
    <section className={s.section} id="transformations">
      <div className={s.inner}>
        <header className={s.header}>
          <h2 className={s.heading}>
            לפני, אחרי, <em className={s.headingEm}>ובין לבין</em>
          </h2>
          <p className={s.sub}>
            אני רואה בראשי את התוצאה הסופית לפני שהיא קיימת - את היופי, האור,
            היחסים והתחושה בחלל, ואין מרגש יותר מהרגע שבו הוא הופך למציאות.
          </p>
        </header>

        <div className={s.grid}>
          {PROJECTS.map((p) => (
            <TransformationCard key={p.id} project={p} />
          ))}
        </div>

        {/* CTA */}
        <button type="button" className={s.cta} onClick={onOpenLead}>
          <span className={s.ctaText}>רוצים שהבית שלכם יהיה הבא? דברו איתי</span>
          <span className={s.ctaArrow} aria-hidden="true">←</span>
        </button>
      </div>
    </section>
  );
}
